import { ID, PaginationResult } from '@/types/common'
import http from '@/utils/request'
import {
  ClassRecordRules,
  ClassMonthlySummary,
  StudentMakeupBalance,
  ClearClassRecordMonthPayload,
  ClassRecordConfigQueryParams,
  ClassRecord,
  ClassRecordQueryParams,
  ClassRecordSummaryParams,
  GenerateClassRecordPayload,
  UpsertCurrentClassConfigPayload,
  BatchUpdateClassRecordPayload,
  StudentClassGenerationBatch,
  StudentClassCurrentConfig,
  UpdateClassRecordPayload,
  StudentClassConfigVersion,
  ManualCreateFromCurrentPayload,
  MonthPlanResult,
  UpdateClassRecordRulesPayload,
} from '../types'

const classRecordApi = {
  async getList(params: ClassRecordQueryParams): Promise<PaginationResult<ClassRecord>> {
    const res = await http.get<PaginationResult<ClassRecord>>('/class-records', {
      params,
    })
    return res.data
  },

  async getSummary(params: ClassRecordSummaryParams): Promise<ClassMonthlySummary> {
    const res = await http.get<ClassMonthlySummary>('/class-records/summary', {
      params,
    })
    return res.data
  },

  async getMonthPlan(studentId: ID, month: string): Promise<MonthPlanResult> {
    const res = await http.get<MonthPlanResult>('/class-records/month-plan', {
      params: { studentId, month },
    })
    return res.data
  },

  async getCurrentConfig(params: ClassRecordConfigQueryParams): Promise<StudentClassCurrentConfig | null> {
    const res = await http.get<StudentClassCurrentConfig | null>('/class-records/current-config', {
      params,
    })
    return res.data
  },

  async getGenerationBatches(params: ClassRecordConfigQueryParams): Promise<StudentClassGenerationBatch[]> {
    const res = await http.get<StudentClassGenerationBatch[]>('/class-records/generation-batches', {
      params,
    })
    return res.data
  },

  async getConfigVersions(params: ClassRecordConfigQueryParams): Promise<StudentClassConfigVersion[]> {
    const res = await http.get<StudentClassConfigVersion[]>('/class-records/config-versions', {
      params,
    })
    return res.data
  },

  async setCurrentVersion(studentId: ID, versionId: ID): Promise<void> {
    await http.post('/class-records/set-current-version', { studentId, versionId })
  },

  async deleteConfigVersion(id: ID): Promise<{ message?: string }> {
    const res = await http.delete<{ message?: string }>(
      `/class-records/config-versions/${id}`,
    )
    return res.data
  },

  async deleteGenerationBatch(id: ID): Promise<{ message?: string }> {
    const res = await http.delete<{ message?: string }>(
      `/class-records/generation-batches/${id}`,
    )
    return res.data
  },

  async generate(data: GenerateClassRecordPayload): Promise<{ message?: string }> {
    const res = await http.post<{ message?: string }>('/class-records/generate', data)
    return res.data
  },

  async upsertCurrentConfig(data: UpsertCurrentClassConfigPayload): Promise<{ message?: string }> {
    const res = await http.post<{ message?: string }>('/class-records/current-config', data)
    return res.data
  },

  async update(id: ID, data: UpdateClassRecordPayload): Promise<void> {
    await http.put(`/class-records/${id}`, data)
  },

  async getRules(): Promise<ClassRecordRules> {
    const res = await http.get<ClassRecordRules>('/class-records/rules')
    return res.data
  },

  async updateRules(data: UpdateClassRecordRulesPayload): Promise<{ message?: string }> {
    const res = await http.put<{ message?: string }>('/class-records/rules', data)
    return res.data
  },

  async getMakeupBalance(studentId: ID): Promise<StudentMakeupBalance> {
    const res = await http.get<StudentMakeupBalance>('/class-records/makeup-balance', {
      params: { studentId },
    })
    return res.data
  },

  async batchUpdate(data: BatchUpdateClassRecordPayload): Promise<{ message?: string }> {
    const res = await http.post<{ message?: string }>('/class-records/batch-update', data)
    return res.data
  },

  async batchDelete(ids: ID[]): Promise<{ message?: string }> {
    const res = await http.post<{ message?: string }>('/class-records/batch-delete', {
      ids,
    })
    return res.data
  },

  async clearMonth(data: ClearClassRecordMonthPayload): Promise<{ message?: string }> {
    const res = await http.post<{ message?: string }>('/class-records/clear-month', data)
    return res.data
  },

  async manualCreateFromCurrent(data: ManualCreateFromCurrentPayload): Promise<{ message?: string }> {
    const res = await http.post<{ message?: string }>(
      '/class-records/manual-create-from-current',
      data,
    )
    return res.data
  },
}

export default classRecordApi
